/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Download,
  Github,
  Loader,
  AlertCircle,
  CheckCircle,
  Package,
  Building2,
  Plus,
} from 'lucide-react';
import { GitHubService, GitHubRepository } from '../../services/githubService';
import { GitLabService, GitLabProject } from '../../services/gitlabService';
import { SkillShopService } from '../../services/skillShopService';
import { GitLabRepoSelector } from './GitLabRepoSelector';
import type { SourceControlProvider } from '../../types';
import type { ProductMetadata, ProductImportResult } from '@shared/types/products';

interface ImportProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: (product: ProductMetadata) => void;
  onCreateNew: (repoUrl: string, repoPath?: string) => void;
  githubService: GitHubService | null;
  isGitHubConfigured: boolean;
  gitlabService?: GitLabService | null;
  isGitLabConfigured?: boolean;
  sourceControlProvider: SourceControlProvider;
  userOrganizationId: string;
  userId?: number;
  accessToken?: string;
  useSSH?: boolean;
}

type ImportStep = 'select-repo' | 'checking' | 'preview' | 'result';

export function ImportProductDialog({
  open,
  onOpenChange,
  onImportComplete,
  onCreateNew,
  githubService,
  isGitHubConfigured,
  gitlabService,
  isGitLabConfigured,
  sourceControlProvider,
  userOrganizationId,
  userId,
  accessToken,
  useSSH,
}: ImportProductDialogProps) {
  const [step, setStep] = useState<ImportStep>('select-repo');
  const [repositories, setRepositories] = useState<(GitHubRepository | GitLabProject)[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [repoError, setRepoError] = useState<string | null>(null);
  const [selectedRepoUrl, setSelectedRepoUrl] = useState<string>('');
  const [repoPath, setRepoPath] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ProductImportResult | null>(null);
  const [previewMetadata, setPreviewMetadata] = useState<ProductMetadata | null>(null);
  
  const [skillShopService] = useState(() => new SkillShopService());

  // Check if source control is configured
  const isSourceControlConfigured =
    sourceControlProvider === 'github' ? isGitHubConfigured : isGitLabConfigured;

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setStep('select-repo');
      setSelectedRepoUrl('');
      setRepoPath('');
      setImportResult(null);
      setPreviewMetadata(null);
      setRepoError(null);
    }
  }, [open]);

  // Load repositories when dialog opens
  useEffect(() => {
    if (!open) return;

    const loadRepositories = async () => {
      if (sourceControlProvider === 'github') {
        if (!githubService || !isGitHubConfigured) {
          setRepositories([]);
          return;
        }

        setLoadingRepos(true);
        setRepoError(null);
        try {
          const repos = await githubService.getRepositories();
          setRepositories(repos);
        } catch (error) {
          console.error('Failed to load repositories:', error);
          setRepoError('Failed to load repositories. Please check your GitHub token in Settings.');
        } finally {
          setLoadingRepos(false);
        }
      } else if (sourceControlProvider === 'gitlab') {
        if (!gitlabService || !isGitLabConfigured) {
          setRepositories([]);
          return;
        }

        setLoadingRepos(true);
        setRepoError(null);
        try {
          const projects = await gitlabService.getProjects();
          setRepositories(projects);
        } catch (error) {
          console.error('Failed to load projects:', error);
          setRepoError('Failed to load projects. Please check your GitLab token in Settings.');
        } finally {
          setLoadingRepos(false);
        }
      }
    };

    loadRepositories();
  }, [open, githubService, isGitHubConfigured, gitlabService, isGitLabConfigured, sourceControlProvider]);

  const handleImport = async () => {
    if (!selectedRepoUrl) return;

    setIsImporting(true);
    setStep('checking');

    try {
      // Always auto-create product if product.md doesn't exist
      // This allows importing any git repository, not just Velocity projects
      // Pass userId to enable auto-join to the product's organization
      const result = await skillShopService.importProductFromRepo(
        selectedRepoUrl,
        userOrganizationId,
        repoPath || undefined,
        accessToken,
        useSSH,
        true, // autoCreate
        userId
      );

      setImportResult(result);

      if (result.success && result.product) {
        // Successful import (either existing product.md or auto-created)
        setPreviewMetadata(result.product);
        setStep('preview');
      } else {
        // Error (needsCreation or other error)
        setStep('result');
      }
    } catch (error: any) {
      console.error('Import failed:', error);
      setImportResult({
        success: false,
        error: error.message || 'Failed to import product',
      });
      setStep('result');
    } finally {
      setIsImporting(false);
    }
  };

  const handleConfirmImport = () => {
    if (previewMetadata) {
      onImportComplete(previewMetadata);
      onOpenChange(false);
    }
  };

  const handleCreateNew = () => {
    onCreateNew(selectedRepoUrl, repoPath || undefined);
    onOpenChange(false);
  };

  const renderRepoSelector = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          Import Repository as Product
        </DialogTitle>
        <DialogDescription>
          Select a repository to import. Any git repository can be imported - if the repository
          already contains product metadata, it will be preserved. Otherwise, a new product will
          be created automatically.
        </DialogDescription>
      </DialogHeader>

      <div className="py-4 space-y-4">
        {/* Repository Selection */}
        {!isSourceControlConfigured ? (
          <div className="p-4 border rounded-lg bg-amber-50 border-amber-200 text-amber-800">
            <p className="font-medium">Source control not configured</p>
            <p className="text-sm mt-1">
              Please configure {sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} in
              Settings to import products from repositories.
            </p>
          </div>
        ) : loadingRepos ? (
          <div className="flex items-center justify-center p-8 text-muted-foreground">
            <Loader className="h-5 w-5 mr-2 animate-spin" />
            Loading repositories...
          </div>
        ) : repoError ? (
          <div className="p-4 border rounded-lg bg-red-50 border-red-200 text-red-800">
            <AlertCircle className="h-4 w-4 inline mr-2" />
            {repoError}
          </div>
        ) : repositories.length === 0 ? (
          <div className="p-4 border rounded-lg bg-amber-50 border-amber-200 text-amber-800">
            No repositories found. Make sure your token has access to at least one repository.
          </div>
        ) : sourceControlProvider === 'gitlab' ? (
          /* GitLab: Three-level selection (group + namespace + project) */
          <div className="space-y-3">
            <GitLabRepoSelector
              projects={repositories as GitLabProject[]}
              selectedRepoUrl={selectedRepoUrl}
              onSelectedRepoUrlChange={setSelectedRepoUrl}
            />

            {/* Repository Path (for monorepos) */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Repository Path <span className="text-muted-foreground">(optional)</span>
              </label>
              <input
                type="text"
                value={repoPath}
                onChange={(e) => setRepoPath(e.target.value)}
                placeholder="e.g., product (for monorepos)"
                className="w-full p-2 border rounded-md bg-background"
              />
              <p className="text-xs text-muted-foreground">
                For monorepos, specify the path to the product directory within the repository.
              </p>
            </div>
          </div>
        ) : (
          /* GitHub: Single flat selector */
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Github className="h-4 w-4" />
                Select Repository
              </label>
              <select
                value={selectedRepoUrl}
                onChange={(e) => setSelectedRepoUrl(e.target.value)}
                className="w-full p-2 border rounded-md bg-background"
              >
                <option value="">Choose a repository...</option>
                {(repositories as GitHubRepository[]).map((repo) => (
                  <option key={repo.full_name} value={repo.html_url}>
                    {repo.full_name} ({repo.private ? 'Private' : 'Public'})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Repository Path <span className="text-muted-foreground">(optional)</span>
              </label>
              <input
                type="text"
                value={repoPath}
                onChange={(e) => setRepoPath(e.target.value)}
                placeholder="e.g., product (for monorepos)"
                className="w-full p-2 border rounded-md bg-background"
              />
              <p className="text-xs text-muted-foreground">
                For monorepos, specify the path to the product directory within the repository.
              </p>
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button onClick={handleImport} disabled={!selectedRepoUrl || isImporting}>
          {isImporting ? (
            <>
              <Loader className="h-4 w-4 mr-2 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Import
            </>
          )}
        </Button>
      </DialogFooter>
    </>
  );

  const renderChecking = () => (
    <>
      <DialogHeader>
        <DialogTitle>Importing Repository</DialogTitle>
        <DialogDescription>Setting up repository and product metadata...</DialogDescription>
      </DialogHeader>

      <div className="py-8 flex flex-col items-center justify-center">
        <Loader className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">
          Cloning repository and configuring product...
        </p>
      </div>
    </>
  );

  const renderPreview = () => {
    const isAutoCreated = importResult?.autoCreated;
    
    return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          {isAutoCreated ? 'Repository Imported' : 'Product Found'}
        </DialogTitle>
        <DialogDescription>
          {isAutoCreated 
            ? 'A new product has been created for this repository. Review and confirm to add it to your organization.'
            : 'Review the product metadata before importing.'
          }
        </DialogDescription>
      </DialogHeader>

      {previewMetadata && (
        <div className="py-4 space-y-4">
          <div className="p-4 border rounded-lg bg-muted/30">
            <div className="flex items-start gap-3">
              <Package className="h-8 w-8 text-primary mt-1" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg">{previewMetadata.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {previewMetadata.description}
                </p>
                <div className="mt-3 space-y-1 text-sm">
                  <p>
                    <span className="font-medium">Product ID:</span>{' '}
                    <code className="bg-muted px-1 rounded">{previewMetadata.productId}</code>
                  </p>
                  <p>
                    <span className="font-medium">Owner:</span> {previewMetadata.owner}
                  </p>
                  {previewMetadata.capabilities.length > 0 && (
                    <p>
                      <span className="font-medium">Capabilities:</span>{' '}
                      {previewMetadata.capabilities.join(', ')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Building2 className="h-4 w-4" />
            <span>This product will be added to your organization.</span>
          </div>
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={() => setStep('select-repo')}>
          Back
        </Button>
        <Button onClick={handleConfirmImport}>
          <CheckCircle className="h-4 w-4 mr-2" />
          Confirm Import
        </Button>
      </DialogFooter>
    </>
    );
  };

  const renderResult = () => {
    const isNeedsCreation = importResult?.needsCreation;
    const isError = !importResult?.success && !isNeedsCreation;

    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isNeedsCreation ? (
              <>
                <Package className="h-5 w-5 text-amber-600" />
                No Product Found
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-red-600" />
                Import Failed
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {isNeedsCreation && (
            <div className="space-y-4">
              <div className="p-4 border rounded-lg bg-amber-50 border-amber-200 text-amber-800">
                <p>
                  This repository doesn't contain a{' '}
                  <code className="bg-amber-100 px-1 rounded">data/product/product.md</code> file.
                </p>
              </div>
              <p className="text-muted-foreground">
                You can create a new product and link it to this repository. The product metadata
                will be saved to the repository.
              </p>
            </div>
          )}

          {isError && (
            <div className="p-4 border rounded-lg bg-red-50 border-red-200 text-red-800">
              <p className="font-medium">Import failed</p>
              <p className="text-sm mt-2">{importResult?.error || 'An unknown error occurred.'}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setStep('select-repo')}>
            Back
          </Button>
          {isNeedsCreation && (
            <Button onClick={handleCreateNew}>
              <Plus className="h-4 w-4 mr-2" />
              Create New Product
            </Button>
          )}
          {isError && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        {step === 'select-repo' && renderRepoSelector()}
        {step === 'checking' && renderChecking()}
        {step === 'preview' && renderPreview()}
        {step === 'result' && renderResult()}
      </DialogContent>
    </Dialog>
  );
}

